import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  FaChartBar,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaSyncAlt,
  FaTint,
} from "react-icons/fa";
import "../styles/SuperAdminWaterUsageComparison.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://depotaircerdas.com/api";

const formatNumber = (value, digits = 2) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(num);
};

const formatLiters = (value) => `${formatNumber(value)} L`;
const formatGallons = (value) => `${formatNumber(value)} galon`;

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("id-ID");
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getEndOfWeek = (date) => {
  const start = getStartOfWeek(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

const getStartOfMonth = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getEndOfMonth = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(23, 59, 59, 999);
  return d;
};

const SummaryBox = ({ icon, title, value, subtitle }) => (
  <div className="water-comparison-summary-box">
    <div className="water-comparison-summary-icon">{icon}</div>
    <div className="water-comparison-summary-content">
      <span>{title}</span>
      <strong>{value}</strong>
      <small>{subtitle}</small>
    </div>
  </div>
);

const SuperAdminWaterUsageComparison = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("daily");

  const authHeaders = useMemo(() => {
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }, [token]);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("is_super_admin");
    navigate("/login", { replace: true });
  }, [navigate]);

  const fetchComparison = useCallback(async () => {
    if (!token) {
      handleUnauthorized();
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${API_BASE_URL}/superadmin/tables/water-usage-comparison`,
        { headers: authHeaders }
      );

      setRows(Array.isArray(response.data?.comparison) ? response.data.comparison : []);
    } catch (err) {
      console.error("Error fetching comparison:", err);
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        handleUnauthorized();
        return;
      }
      setError(err.response?.data?.message || "Gagal mengambil data perbandingan.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, token, handleUnauthorized]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  const now = useMemo(() => new Date(), []);
  const dailyPeriodText = useMemo(() => {
    return `Untuk tanggal ${formatDateOnly(now)}`;
  }, [now]);

  const weeklyPeriodText = useMemo(() => {
    const start = getStartOfWeek(now);
    const end = getEndOfWeek(now);
    return `Periode ${formatDateOnly(start)} - ${formatDateOnly(end)}`;
  }, [now]);

  const monthlyPeriodText = useMemo(() => {
    const start = getStartOfMonth(now);
    const end = getEndOfMonth(now);
    return `Periode ${formatDateOnly(start)} - ${formatDateOnly(end)}`;
  }, [now]);

  const sortedRows = useMemo(() => {
    const cloned = [...rows];

    if (mode === "daily") {
      cloned.sort((a, b) => Number(b.daily_volume || 0) - Number(a.daily_volume || 0));
    } else if (mode === "weekly") {
      cloned.sort((a, b) => Number(b.weekly_volume || 0) - Number(a.weekly_volume || 0));
    } else {
      cloned.sort((a, b) => Number(b.monthly_volume || 0) - Number(a.monthly_volume || 0));
    }

    return cloned;
  }, [rows, mode]);

  const labels = useMemo(() => {
    return sortedRows.map((item) => item.depot_name || item.username);
  }, [sortedRows]);

  const chartDatasetVolume = useMemo(() => {
    if (mode === "daily") return sortedRows.map((item) => Number(item.daily_volume || 0));
    if (mode === "weekly") return sortedRows.map((item) => Number(item.weekly_volume || 0));
    return sortedRows.map((item) => Number(item.monthly_volume || 0));
  }, [sortedRows, mode]);

  const chartDatasetGallons = useMemo(() => {
    if (mode === "daily") return sortedRows.map((item) => Number(item.daily_gallons || 0));
    if (mode === "weekly") return sortedRows.map((item) => Number(item.weekly_gallons || 0));
    return sortedRows.map((item) => Number(item.monthly_gallons || 0));
  }, [sortedRows, mode]);

  const chartTitle = useMemo(() => {
    if (mode === "daily") return "Perbandingan Total Penggunaan Air Harian Antar Akun";
    if (mode === "weekly") return "Perbandingan Total Penggunaan Air Mingguan Antar Akun";
    return "Perbandingan Total Penggunaan Air Bulanan Antar Akun";
  }, [mode]);

  const activePeriodText = useMemo(() => {
    if (mode === "daily") return dailyPeriodText;
    if (mode === "weekly") return weeklyPeriodText;
    return monthlyPeriodText;
  }, [mode, dailyPeriodText, weeklyPeriodText, monthlyPeriodText]);

  const chartData = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          label: "Volume Air (L)",
          data: chartDatasetVolume,
          backgroundColor: "rgba(14, 165, 233, 0.75)",
          borderColor: "#0284c7",
          borderWidth: 1,
        },
        {
          label: "Total Galon",
          data: chartDatasetGallons,
          backgroundColor: "rgba(34, 197, 94, 0.65)",
          borderColor: "#16a34a",
          borderWidth: 1,
        },
      ],
    };
  }, [labels, chartDatasetVolume, chartDatasetGallons]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
        },
        title: {
          display: true,
          text: chartTitle,
        },
      },
      scales: {
        x: {
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 20,
          },
          title: {
            display: true,
            text: "Akun",
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Nilai Pemakaian",
          },
        },
      },
    };
  }, [chartTitle]);

  const totalAccounts = rows.length;

  const topAccount = useMemo(() => {
    if (!sortedRows.length) return null;
    return sortedRows[0];
  }, [sortedRows]);

  const totalAllAccounts = useMemo(() => {
    return sortedRows.reduce((sum, item) => {
      if (mode === "daily") return sum + Number(item.daily_volume || 0);
      if (mode === "weekly") return sum + Number(item.weekly_volume || 0);
      return sum + Number(item.monthly_volume || 0);
    }, 0);
  }, [sortedRows, mode]);

  if (loading) {
    return <div className="water-comparison-page-state">Memuat data perbandingan akun...</div>;
  }

  return (
    <div className="superadmin-water-comparison-container">
      <div className="water-comparison-header">
        <div>
          <h2>Perbandingan Penggunaan Air Antar Akun</h2>
          <p>
            Halaman ini membandingkan total penggunaan air seluruh akun berdasarkan
            total gabungan semua sensor air pada masing-masing akun.
          </p>
        </div>

        <button className="water-comparison-refresh-btn" onClick={fetchComparison}>
          <FaSyncAlt /> Refresh
        </button>
      </div>

      {error && <div className="water-comparison-error-box">{error}</div>}

      <div className="water-comparison-mode-switcher">
        <button className={mode === "daily" ? "active" : ""} onClick={() => setMode("daily")}>
          <FaCalendarDay /> Harian
        </button>
        <button className={mode === "weekly" ? "active" : ""} onClick={() => setMode("weekly")}>
          <FaCalendarWeek /> Mingguan
        </button>
        <button className={mode === "monthly" ? "active" : ""} onClick={() => setMode("monthly")}>
          <FaCalendarAlt /> Bulanan
        </button>
      </div>

      <div className="water-comparison-period-box">
        <strong>Periode aktif:</strong> {activePeriodText}
      </div>

      <div className="water-comparison-summary-grid">
        <SummaryBox
          icon={<FaTint />}
          title="Total Akun"
          value={String(totalAccounts)}
          subtitle="Jumlah akun yang dibandingkan"
        />
        <SummaryBox
          icon={<FaChartBar />}
          title="Total Pemakaian Semua Akun"
          value={formatLiters(totalAllAccounts)}
          subtitle={activePeriodText}
        />
        <SummaryBox
          icon={<FaTint />}
          title="Akun Pemakaian Tertinggi"
          value={topAccount ? (topAccount.depot_name || topAccount.username) : "-"}
          subtitle={
            topAccount
              ? mode === "daily"
                ? formatLiters(topAccount.daily_volume)
                : mode === "weekly"
                ? formatLiters(topAccount.weekly_volume)
                : formatLiters(topAccount.monthly_volume)
              : "-"
          }
        />
      </div>

      <div className="water-comparison-chart-card">
        {sortedRows.length === 0 ? (
          <div className="water-comparison-page-state small">
            Belum ada data penggunaan air yang bisa dibandingkan.
          </div>
        ) : (
          <div className="water-comparison-chart-wrapper">
            <Bar data={chartData} options={chartOptions} />
          </div>
        )}
      </div>

      <div className="water-comparison-table-card">
        <div className="water-comparison-table-header">
          <h3>Rincian Perbandingan Antar Akun</h3>
          <span>{activePeriodText}</span>
        </div>

        <div className="water-comparison-table-wrapper">
          <table className="water-comparison-table">
            <thead>
              <tr>
                <th>Peringkat</th>
                <th>Username</th>
                <th>Nama Depot / Rumah</th>
                <th>Jumlah Sensor Air</th>
                <th>Harian</th>
                <th>Mingguan</th>
                <th>Bulanan</th>
                <th>Total Record</th>
                <th>Update Terakhir</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan="9" className="empty-cell">
                    Tidak ada data akun.
                  </td>
                </tr>
              ) : (
                sortedRows.map((item, index) => (
                  <tr key={item.id || item.username}>
                    <td>{index + 1}</td>
                    <td>{item.username}</td>
                    <td>{item.depot_name || "-"}</td>
                    <td>{item.total_sensors || 0}</td>
                    <td>
                      <strong>{formatLiters(item.daily_volume)}</strong>
                      <small>{formatGallons(item.daily_gallons)}</small>
                    </td>
                    <td>
                      <strong>{formatLiters(item.weekly_volume)}</strong>
                      <small>{formatGallons(item.weekly_gallons)}</small>
                    </td>
                    <td>
                      <strong>{formatLiters(item.monthly_volume)}</strong>
                      <small>{formatGallons(item.monthly_gallons)}</small>
                    </td>
                    <td>{item.total_records || 0}</td>
                    <td>{formatDateTime(item.latest_timestamp)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminWaterUsageComparison;